/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { ThemeProvider } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
// @ts-ignore
import { ToggleButton, Button, LogOut as LogOutIcon } from '@netbasequid/core'
import ErrorMessage from 'components/share/ErrorMessage'
import {
  StyledDashboardMain,
  StyledDashboardContent,
  StyledGridLayout,
  StyledGrid,
  GlobalStyleForDashboard,
  ToggleLabel,
  StyledLabelText,
  StyledDashboardHeader,
  StyledDashboardTitleContainer,
  StyledDashboardTitle,
  StyledDashboardController
} from 'pages/dashboard/main/index.css'
import WidgetFactory from 'components/widget/WidgetFactory'
import WidgetContainer from 'components/dashboard/WidgetContainer'
import PlaceHolder from 'components/share/PlaceHolder'
import exportPdf from 'pages/dashboard/main/pdfExport/pdfExporter'
import PdfExportButton from 'pages/dashboard/main/pdfExport/PdfExportButton'
import { Page } from 'features/ui/types'
import { useLiveReportUniversalError, useIsProtectedLiveReport } from 'hooks/useLiveReportState'
import ScreenSpinner from 'components/share/ScreenSpinner'
import { setAutoId, AutomationId } from 'utils/automationUtil'
import { ErrorCodes } from 'liveReportService'
import useActionPermissions from 'pages/dashboard/view/hooks/useActionPermissions'
import { ErrorCodeToTranslationKey } from '../utils'
import {
  useRetrieveDashboard,
  useRetrieveTheme,
  useRetrievePageConfig,
  useRetrieveHeartbeat,
  useRetrieveReportConfig,
  useRetrieveEmojis
} from './hooks'
import Footer from './Footer'
import { useLiveReportAuth } from '../hooks'
import ErrorDialog from './ErrorDialog'

interface ReportProps {
  id: string
}

const Report = ({ id }: ReportProps) => {
  const { status: dashboardStatus, dashboardData, rowHeight, statusCode } = useRetrieveDashboard(id)
  const { displayTheme, muiTheme, setTheme } = useRetrieveTheme()
  const { status: pageConfigStatus } = useRetrievePageConfig()
  useRetrieveReportConfig()
  const isProtectedReport = useIsProtectedLiveReport()
  const { t } = useTranslation()
  const { handleLogout, notifySessionInvalid } = useLiveReportAuth()
  const navigate = useNavigate()
  const { status: emojiStatus } = useRetrieveEmojis()
  const { pdfExportPermission } = useActionPermissions()

  const isLoading = [dashboardStatus, pageConfigStatus, emojiStatus].some(
    (status) => status === 'loading'
  )
  const isFailed = [dashboardStatus, pageConfigStatus, emojiStatus].some(
    (status) => status === 'failed'
  )
  const isSucceeded = [dashboardStatus, pageConfigStatus, emojiStatus].every(
    (status) => status === 'succeeded'
  )

  // Long polling heartbeat
  useRetrieveHeartbeat()

  // For “Unprotected Live Reports”, there will be no Log Out button. End the user session when the tab is closed.
  useEffect(() => {
    if (!isProtectedReport) {
      window.addEventListener('beforeunload', handleLogout)
    }
    return () => {
      if (!isProtectedReport) {
        window.removeEventListener('beforeunload', handleLogout)
      }
    }
  }, [isProtectedReport, handleLogout])

  // PDF export
  const dashboardRef = useRef<HTMLDivElement>(null!)
  const headerRef = useRef<HTMLDivElement>(null!)
  const exportDashboardToPdf = async () => {
    if (dashboardRef) {
      await exportPdf(dashboardData!, dashboardRef, [headerRef], Page.LiveReport)
    }
  }

  // handle multiple login and session timeout error dialog
  const error = useLiveReportUniversalError()
  const hasMultipleLoginError = isProtectedReport && error?.status === ErrorCodes.SessionKicked
  const hasSessionTimeoutError = !isProtectedReport && error?.status === ErrorCodes.InvalidSessionId
  const openDialog = hasMultipleLoginError || hasSessionTimeoutError
  const handleClose = isProtectedReport ? notifySessionInvalid : () => navigate(0)
  const okButtonText = hasSessionTimeoutError ? t('refresh') : t('ok')

  return (
    <>
      {isLoading && <ScreenSpinner />}
      {isFailed && (
        // @ts-ignore
        <StyledDashboardMain displayTheme={displayTheme} isPresentationMode>
          <StyledDashboardHeader>
            <StyledDashboardTitleContainer />
            <StyledDashboardController>
              <ToggleLabel>
                {/* @ts-ignore */}
                <StyledLabelText displayTheme={displayTheme}>{t('darkMode')}</StyledLabelText>
                <ToggleButton
                  mode={displayTheme}
                  checked={displayTheme === 'dark'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const selectedTheme = e.target.checked ? 'dark' : 'light'
                    setTheme(selectedTheme)
                  }}
                />
              </ToggleLabel>
              {isProtectedReport && (
                <Button mode={displayTheme} color="secondary" onClick={handleLogout}>
                  <LogOutIcon mode={displayTheme} />
                  <span style={{ marginLeft: '6px' }}>{t('logOut')}</span>
                </Button>
              )}
            </StyledDashboardController>
          </StyledDashboardHeader>
          <StyledDashboardContent>
            <ErrorMessage>
              {t(ErrorCodeToTranslationKey[statusCode as ErrorCodes] || 'unexpectedError')}
            </ErrorMessage>
          </StyledDashboardContent>
        </StyledDashboardMain>
      )}
      {isSucceeded && (
        // TODO: ThemeProvider is for WaveLoading component which uses mui skeleton. Should be removed when WaveLoading is replaced by @netbaseuid/skeleton.
        <ThemeProvider theme={muiTheme}>
          {/* @ts-ignore */}
          <GlobalStyleForDashboard displayTheme={displayTheme} isPresentationMode />
          {/* @ts-ignore */}
          <StyledDashboardMain displayTheme={displayTheme} ref={dashboardRef}>
            <StyledDashboardHeader ref={headerRef}>
              <StyledDashboardTitleContainer>
                <StyledDashboardTitle
                  /* @ts-ignore */
                  displayTheme={displayTheme}
                  {...setAutoId(AutomationId.liveReport.title)}>
                  {dashboardData?.name}
                </StyledDashboardTitle>
              </StyledDashboardTitleContainer>
              <StyledDashboardController>
                <ToggleLabel>
                  {/* @ts-ignore */}
                  <StyledLabelText displayTheme={displayTheme}>{t('darkMode')}</StyledLabelText>
                  <ToggleButton
                    mode={displayTheme}
                    checked={displayTheme === 'dark'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const selectedTheme = e.target.checked ? 'dark' : 'light'
                      setTheme(selectedTheme)
                    }}
                  />
                </ToggleLabel>
                {pdfExportPermission && <PdfExportButton handleExport={exportDashboardToPdf} />}
                {isProtectedReport && (
                  <Button
                    mode={displayTheme}
                    color="secondary"
                    onClick={handleLogout}
                    {...setAutoId(AutomationId.liveReport.logoutButton)}>
                    <LogOutIcon mode={displayTheme} />
                    <span style={{ marginLeft: '6px' }}>{t('logOut')}</span>
                  </Button>
                )}
              </StyledDashboardController>
            </StyledDashboardHeader>
            <StyledGridLayout
              cols={dashboardData?.numOfColumn}
              rowHeight={rowHeight}
              margin={[16, 16]}
              containerPadding={[0, 0]}
              measureBeforeMount
              useCSSTransforms={false}
              isDraggable={false}
              isResizable={false}
              isBounded>
              {dashboardData?.widgets?.map((widget) => {
                const widgetLayout = {
                  i: widget.widgetId.toString(),
                  x: widget.posX - 1,
                  y: widget.posY - 1,
                  w: widget.sizeX,
                  h: widget.sizeY
                }
                return (
                  <StyledGrid key={widget.widgetId} data-grid={widgetLayout}>
                    <ErrorBoundary
                      FallbackComponent={(e) => <PlaceHolder>{e.error.message}</PlaceHolder>}>
                      <WidgetContainer
                        widgetLayout={widgetLayout}
                        widgetSetting={widget}
                        displayTheme={displayTheme}>
                        <WidgetFactory type={widget.type} widgetId={widget.widgetId} />
                      </WidgetContainer>
                    </ErrorBoundary>
                  </StyledGrid>
                )
              })}
            </StyledGridLayout>
            <Footer />
          </StyledDashboardMain>
          {(hasMultipleLoginError || hasSessionTimeoutError) && (
            <ErrorDialog
              open={openDialog}
              onClose={handleClose}
              errorCode={error!.status}
              okButtonText={okButtonText}
            />
          )}
        </ThemeProvider>
      )}
    </>
  )
}

export default Report
